import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import Loader from 'src/components/loader';
import { api, apiWithContext } from 'src/utils/api';
import { API_RESOURCES, PAGINATION_IGNORE_DEFAULT_LIMIT } from 'src/utils/constants';
import { getUuid } from 'src/utils/url';
import { getWorkInstructionReportsByPrintUri } from 'src/utils/work-instructions'; 

import CollapsableCard from '../collapsable-card';
import WorkflowStep from './_workflow-step';

/**
 * Contains workflow steps for a piece
 */
const Workflow = ({ pieceURI, workflowID }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [prints, setPrints] = useState(null);
  const [workChecklistLinkings, setWorkChecklistLinkings] = useState([]);
  const [workInstructionReports, setWorkInstructionReports] = useState({});
  const { addToast } = useToasts();

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const [workflow, { resources: prints }] = await Promise.all([
          api.get(`${API_RESOURCES.WORKFLOW}/${workflowID}`).json(),
          api.get(`${API_RESOURCES.PRINT}/`, {
              searchParams: {
                'filter[piece]': pieceURI,
                'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
              },
            }
          ).json(),
        ]);

        setWorkflow(workflow);
        setPrints(prints);

        const checklistResponse = await api.get(
          `${API_RESOURCES.WORK_CHECKLIST_FOR_RESOURCE}/`,{
            searchParams: {
              'filter[related_uri]': pieceURI,
              'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
            },
        }).json();

        const group = checklistResponse.resources?.find(g => g.related_uri === pieceURI);
        setWorkChecklistLinkings(group?.work_checklist_linkings || []);

        const workInstructions = group?.work_checklist_linkings?.flatMap(link => link.work_checklist.work_instructions) || [];
        const reportTypes = workInstructions.reduce((acc, wi) => {
          acc[wi.uuid] = wi.report_type;
          return acc;
        }, {});

        const apiEnhanced = apiWithContext({ features: {} });
        const reportsByPrintUri = await getWorkInstructionReportsByPrintUri(apiEnhanced, prints, reportTypes);
        setWorkInstructionReports(reportsByPrintUri);

      } catch (error) {
        addToast('There was an error. Please try again.', { appearance: 'error' });
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  if (!workflow || !prints || isLoading) {
    return <Loader />;
  }

  return (
    <CollapsableCard title="Workflow Overview:" subTitle={workflow.name}>
      {
      // eslint-disable-next-line camelcase
      workflow.process_steps.map((processStep, index) => {
        const stepId = getUuid(processStep);
        const print = prints.find((print) => print.process_step === processStep);
        const report = workInstructionReports[print?.uri];

        const stepChecklistLinkings = workChecklistLinkings.find(
          link => link.process_step === processStep
        );

        return (
          <WorkflowStep
            key={stepId}
            status={print?.status}
            index={index}
            stepID={stepId}
            checkListLinkings={stepChecklistLinkings}
            report={report}
          />
        )
      })
    }
    </CollapsableCard>
  );
};

Workflow.propTypes = {
  pieceURI: PropTypes.string.isRequired,
  workflowID: PropTypes.string.isRequired,
};

export default Workflow;
